import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Fade from "@material-ui/core/Fade";
import Box from "@material-ui/core/Box";
import commons from "./../common/commons";

const ROTATION_INTERVAL_MS = 12000;

const styles = (theme) => ({
  promoTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem",
      marginBottom: theme.spacing(1.5),
    },
  },
  productImageLink: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "block",
    overflow: "hidden",
  },
  productHero: {
    position: "relative",
    width: "100%",
    height: 185,
    marginBottom: theme.spacing(2),
    overflow: "visible",
    [theme.breakpoints.up("md")]: {
      height: 215,
    },
  },
  productImage: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "138%",
    maxWidth: "none",
    height: "138%",
    objectFit: "contain",
    display: "block",
    transform: "translate(-50%, -46%)",
    [theme.breakpoints.up("md")]: {
      width: "145%",
      height: "145%",
    },
  },
  price: {
    position: "absolute",
    left: "50%",
    bottom: -15,
    transform: "translateX(-50%)",
    zIndex: 1,
    pointerEvents: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.12rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
    padding: theme.spacing(0.75, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: "rgba(255, 255, 255, 0.78)",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
    textShadow: "0 1px 2px rgba(255, 255, 255, 0.85)",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.28rem",
    },
  },
  ctaButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
    color: "white",
    fontWeight: "bold",
    padding: theme.spacing(1.5, 3),
    fontSize: "0.9rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "40px",
    maxWidth: 240,
    width: "100%",
    "&:hover": {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
      boxShadow: theme.shadows[8],
    },
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1.25, 3.5),
      fontSize: "0.95rem",
    },
  },
  dots: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.grey[300],
    transition: "background-color 0.3s ease",
    cursor: "pointer",
    border: "none",
    padding: 0,
  },
  dotActive: {
    backgroundColor: theme.palette.primary.main,
  },
  fadeWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
});

class SalesPromoBox extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentIndex: 0,
      visible: true,
    };
    this.intervalId = null;
    this.timeoutIds = [];
  }

  componentDidMount() {
    if (this.props.promos.length > 1) {
      this.startRotation();
    }
  }

  componentWillUnmount() {
    this.clearRotation();
    this.clearTimeouts();
  }

  scheduleTimeout(callback, delay) {
    const timeoutId = setTimeout(() => {
      this.timeoutIds = this.timeoutIds.filter((id) => id !== timeoutId);
      callback();
    }, delay);
    this.timeoutIds.push(timeoutId);
  }

  clearTimeouts() {
    this.timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutIds = [];
  }

  startRotation() {
    this.intervalId = setInterval(() => {
      this.setState({ visible: false });
      // After fade out, switch slide and fade back in
      this.scheduleTimeout(() => {
        this.setState((prevState) => ({
          currentIndex: (prevState.currentIndex + 1) % this.props.promos.length,
          visible: true,
        }));
      }, 300);
    }, ROTATION_INTERVAL_MS);
  }

  clearRotation() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  handleDotClick = (index) => {
    this.clearRotation();
    this.clearTimeouts();
    this.setState({ visible: false });
    this.scheduleTimeout(() => {
      this.setState({ currentIndex: index, visible: true });
      if (this.props.promos.length > 1) {
        this.startRotation();
      }
    }, 200);
  };

  render() {
    const { classes, promos } = this.props;
    const { currentIndex, visible } = this.state;

    if (!promos || promos.length === 0) return null;

    const promo = promos[currentIndex];
    const { url: imageUrl } = commons.getProductPrimaryImage(
      Array.isArray(promo.cdn) && promo.cdn.length > 0 ? promo.cdn : false,
      promo.ean
    );
    const productLink = `/tire/moto/${promo.ean}/${promo.name}`;
    const price = Number(promo.price);
    const hasPrice = Number.isFinite(price) && price > 0;

    return (
      <Box style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Fade in={visible} timeout={300}>
          <Box className={classes.fadeWrapper}>
            <Typography className={classes.promoTitle}>
              {promo.promoTitle}
            </Typography>

            <Box className={classes.productHero}>
              <Link to={productLink} className={classes.productImageLink}>
                <img
                  src={imageUrl}
                  alt={promo.brand || promo.promoTitle}
                  className={classes.productImage}
                />
              </Link>

              {hasPrice && (
                <Typography className={classes.price}>
                  {price.toFixed(2)} ₪
                </Typography>
              )}
            </Box>

            <Button
              component={Link}
              to={productLink}
              variant="contained"
              className={classes.ctaButton}
            >
              {promo.ctaText || "קנה עכשיו"}
            </Button>
          </Box>
        </Fade>

        {promos.length > 1 && (
          <Box className={classes.dots}>
            {promos.map((_, idx) => (
              <button
                key={idx}
                className={`${classes.dot} ${idx === currentIndex ? classes.dotActive : ""}`}
                onClick={() => this.handleDotClick(idx)}
                aria-label={`פרומו ${idx + 1}`}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }
}

SalesPromoBox.propTypes = {
  classes: PropTypes.object.isRequired,
  promos: PropTypes.arrayOf(
    PropTypes.shape({
      ean: PropTypes.string.isRequired,
      name: PropTypes.string,
      displayName: PropTypes.string,
      brand: PropTypes.string,
      displaySize: PropTypes.string,
      price: PropTypes.number,
      cdn: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
      promoTitle: PropTypes.string.isRequired,
      ctaText: PropTypes.string,
    })
  ).isRequired,
};

export default withStyles(styles)(SalesPromoBox);
